import React from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { ChartData } from './types';
import { CindraBarChart } from './CindraBarChart';
import { CindraLineChart } from './CindraLineChart';
import { CindraAreaChart } from './CindraAreaChart';
import { CindraHistogram } from './CindraHistogram';
import { renderPhiStatusTag } from '../../lib/phiStatusTags';
// import { CindraScatterChart } from './CindraScatterChart';
// import { CindraDonutChart } from './CindraDonutChart';
// import { CindraSparkChart } from './CindraSparkChart';

interface CindraChartRendererProps {
  chartData: ChartData;
  patientName?: string;
  phiAnonymized?: boolean;
  phiDeanonymized?: boolean;
  phiDeanonymizationEnabled?: boolean;
  phiEncryptedMapAvailable?: boolean;
  phiViolation?: boolean;
}

export function CindraChartRenderer({ chartData, patientName, phiAnonymized, phiDeanonymized, phiDeanonymizationEnabled, phiEncryptedMapAvailable, phiViolation }: CindraChartRendererProps) {
  const { type, config } = chartData;
  const phiTag = renderPhiStatusTag(phiAnonymized, phiDeanonymized, phiDeanonymizationEnabled, phiEncryptedMapAvailable, phiViolation);

  const handleCopyConfig = async () => {
    const configJson = JSON.stringify(chartData, null, 2);
    try {
      await navigator.clipboard.writeText(configJson);
    } catch (err) {
      console.error('Failed to copy chart config:', err);
    }
  };

  const renderChart = () => {
    try {
      switch (type) {
        case 'bar':
          return <CindraBarChart config={config} />;
        case 'line':
          return <CindraLineChart config={config} />;
        case 'area':
          return <CindraAreaChart config={config} />;
        case 'histogram':
          return <CindraHistogram config={config} />;
        // case 'scatter':
        //   return <CindraScatterChart config={config} />;
        // case 'donut':
        //   return <CindraDonutChart config={config} />;
        // case 'spark':
        //   return <CindraSparkChart config={config} />;
        default:
          return (
            <div className="text-red-600 text-sm">
              Unsupported chart type: {type}
            </div>
          );
      }
    } catch (error) {
      return (
        <div className="text-red-600 text-sm">
          Error rendering chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  };

  return (
    <div>
      {/* Title row with copy button and patient tag */}
      {config.title && (
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">{config.title}</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopyConfig}
              className="p-1 rounded-md bg-white border border-gray-300 shadow-sm hover:bg-gray-100 active:scale-90 transition-all"
              title="Copy chart configuration"
            >
              <ClipboardDocumentIcon className="h-4 w-4 text-gray-600" />
            </button>
            {patientName && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                {patientName}
              </span>
            )}
            {phiTag}
          </div>
        </div>
      )}

      {/* Chart */}
      {renderChart()}
    </div>
  );
}
